import React from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import { PageLoader } from '../../components/common/Loader';
import { IconTrash, IconAlertTriangle, IconClock } from '@tabler/icons-react';
import { usePurgeQueue } from '../../hooks/useDocuments';

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const CATEGORY_LABELS = {
    cv_resume: 'CV / Résumé', cover_letter: 'Cover Letter', certificate: 'Certificate',
    id_document: 'ID Document', contract: 'Contract', appraisal: 'Appraisal',
    training: 'Training', office_memo: 'Office Memo', policy: 'Policy',
    personal: 'Personal', other: 'Other',
};

const DEPARTURE_COLORS = {
    resigned:    'bg-warning/10 text-warning',
    terminated:  'bg-danger/10 text-danger',
    retired:     'bg-info/10 text-info',
    contract_end: 'bg-purple/10 text-purple',
};

export default function PurgeQueue() {
    const { data, isLoading } = usePurgeQueue();
    const queue = data?.purge_queue || [];

    if (isLoading) return <PageLoader text="Loading purge queue..." />;

    return (
        <div className="flex flex-col gap-20 pb-30">
            <Breadcrumb title="Purge Queue" path="Documents" />

            {queue.length > 0 && (
                <div className="flex items-center gap-10 px-15 py-10 bg-danger/5 border border-danger/20 rounded-lg">
                    <IconAlertTriangle size={15} className="text-danger flex-shrink-0" />
                    <p className="text-[12px] text-text-primary">
                        <span className="font-bold">{queue.length} document{queue.length !== 1 ? 's' : ''}</span> scheduled for permanent deletion.
                        Review before the purge date.
                    </p>
                </div>
            )}

            {queue.length === 0 ? (
                <div className="bg-bg-dark rounded-lg border border-border shadow-light py-60 text-center">
                    <div className="w-44 h-44 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-12">
                        <IconTrash className="text-success" size={20} />
                    </div>
                    <p className="text-sm font-semibold text-text-primary mb-4">Purge queue is empty</p>
                    <p className="text-[11px] text-text-secondary">Documents scheduled for deletion will appear here.</p>
                </div>
            ) : (
                <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                    <div className="px-20 py-12 border-b border-border bg-bg-light/30 flex items-center justify-between">
                        <h3 className="text-[13px] font-semibold text-text-primary">Scheduled for Deletion</h3>
                        <span className="text-[11px] text-text-secondary">{queue.length} item{queue.length !== 1 ? 's' : ''}</span>
                    </div>
                    {queue.map((item, i) => {
                        const executed  = !!item.executed_at;
                        const deptColor = DEPARTURE_COLORS[item.departure_status] || 'bg-bg-light text-text-secondary';
                        return (
                            <div key={item.id} className={`flex items-start gap-12 px-15 py-10 ${i > 0 ? 'border-t border-border' : ''}`}>
                                <div className="w-[34px] h-[34px] rounded-lg bg-danger/10 flex items-center justify-center flex-shrink-0 mt-1">
                                    <IconTrash size={14} className="text-danger" />
                                </div>
                                <div className="flex-grow min-w-0">
                                    <p className="text-[13px] font-semibold text-text-primary truncate">{item.document_title}</p>
                                    <div className="flex flex-wrap items-center gap-8 mt-2">
                                        <span className="text-[10px] text-text-secondary">
                                            {CATEGORY_LABELS[item.document_category] || item.document_category}
                                        </span>
                                        <span className="text-text-secondary/30">·</span>
                                        <span className={`text-[9px] font-bold uppercase tracking-widest px-5 py-1 rounded-full ${deptColor}`}>
                                            {item.departure_status?.replace('_', ' ')}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-5 mt-5">
                                        <IconClock size={10} className={executed ? 'text-success' : 'text-danger'} />
                                        <span className={`text-[10px] font-semibold ${executed ? 'text-success' : 'text-danger'}`}>
                                            {executed
                                                ? `Deleted ${formatDate(item.executed_at)}`
                                                : `Purge after ${formatDate(item.purge_after)}`
                                            }
                                        </span>
                                    </div>
                                </div>
                                <span className={`text-[9px] font-bold uppercase tracking-widest px-6 py-2 rounded-full flex-shrink-0 mt-1 ${
                                    executed ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                                }`}>
                                    {executed ? 'Deleted' : 'Pending'}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
